export interface ScannedProduct {
  title: string
  editionLabel: string
  priceNumber: number
  priceText: string
  thumbnailUrl: string
}

// ---- Helpers ----
function parsePriceNumber(raw: string): number | null {
  // Try patterns like "$14", "14 USD", "USD 14.00", "$14.99"
  const match = raw.match(/(?:USD\s*)?\$?\s*(\d+(?:[\.,]\d{1,2})?)\s*(?:USD)?/i)
  if (!match) return null
  const n = parseFloat(match[1].replace(',', '.'))
  return isNaN(n) ? null : n
}

function formatPriceText(n: number): string {
  return `$${n.toFixed(0)} USD`
}

// ---- JSON-LD scanner ----
function scanJsonLd(): ScannedProduct[] {
  const out: ScannedProduct[] = []
  if (typeof document === 'undefined') return out
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[type="application/ld+json"]',
  )
  scripts.forEach((s) => {
    try {
      const data = JSON.parse(s.textContent || '')
      const items = Array.isArray(data) ? data : [data]
      items.forEach((item: any) => {
        if (!item || typeof item !== 'object') return
        const type = item['@type']
        if (
          type === 'Product' ||
          (Array.isArray(type) && type.includes('Product'))
        ) {
          const offer = Array.isArray(item.offers)
            ? item.offers[0]
            : item.offers
          const priceRaw = offer?.price ?? item.price
          const priceNum =
            typeof priceRaw === 'number'
              ? priceRaw
              : typeof priceRaw === 'string'
                ? parsePriceNumber(priceRaw)
                : null
          const image = Array.isArray(item.image) ? item.image[0] : item.image
          if (item.name && priceNum != null) {
            out.push({
              title: String(item.name),
              editionLabel: 'Programa Digital',
              priceNumber: priceNum,
              priceText: formatPriceText(priceNum),
              thumbnailUrl: image || '',
            })
          }
        }
      })
    } catch {
      /* ignore */
    }
  })
  return out
}

// ---- DOM heuristic scanner ----
function scanDomHeuristics(): ScannedProduct[] {
  if (typeof document === 'undefined') return []
  const out: ScannedProduct[] = []
  const priceRe = /\$\s?\d{1,5}(?:[\.,]\d{2})?/

  // Look at images that are near price-bearing text and a heading
  const images = document.querySelectorAll<HTMLImageElement>('img')
  images.forEach((img) => {
    if (!img.src || img.width < 60 || img.height < 60) return
    const container = img.closest(
      'article, section, [data-product], li, button, div',
    )
    if (!container) return
    const text = container.textContent || ''
    const priceMatch = text.match(priceRe)
    if (!priceMatch) return
    const priceNum = parsePriceNumber(priceMatch[0])
    if (priceNum == null || priceNum <= 0) return

    // Find a likely title (heading or strong text inside container)
    const heading = container.querySelector('h1, h2, h3, h4, h5, h6, strong')
    const titleText = heading?.textContent?.trim() || ''
    if (!titleText || titleText.length < 3 || titleText.length > 120) return

    out.push({
      title: titleText,
      editionLabel: 'Producto Digital',
      priceNumber: priceNum,
      priceText: formatPriceText(priceNum),
      thumbnailUrl: img.src,
    })
  })

  // Deduplicate by title
  const seen = new Set<string>()
  return out.filter((p) => {
    const k = p.title.toLowerCase()
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
}

// ---- Fallback (this page's known products) ----
const FALLBACK_PRODUCTS: ScannedProduct[] = [
  {
    title: 'Menopause Reset + 3 BONOS',
    editionLabel: 'Programa Completo',
    priceNumber: 14,
    priceText: '$14 USD',
    thumbnailUrl:
      'https://cdn.magicpatterns.com/uploads/pCcM5Dc4cgTU6PvK7ojeSo/Mockup_Principal_Pagina.png',
  },
  {
    title: '21 Desayunos Hormonales',
    editionLabel: 'Bono — Recetario',
    priceNumber: 9,
    priceText: '$9 USD',
    thumbnailUrl:
      'https://cdn.magicpatterns.com/uploads/rxxpFw7NBE4Y3XMKuucTtt/Mockup_21_Desayunos.png',
  },
  {
    title: 'Guía de Rescate Nocturno',
    editionLabel: 'Bono — Audio + Guía',
    priceNumber: 7,
    priceText: '$7 USD',
    thumbnailUrl:
      'https://cdn.magicpatterns.com/uploads/d9A54kbHnBcUL5XoeZc6jP/recate_nocturno_final.png',
  },
]

export function scanProducts(): ScannedProduct[] {
  const jsonLd = scanJsonLd()
  if (jsonLd.length > 0) return jsonLd

  const domHits = scanDomHeuristics()
  if (domHits.length > 0) return domHits

  return FALLBACK_PRODUCTS
}

// Weighted random pick — main product chosen more often
export function pickWeightedProduct(
  products: ScannedProduct[],
): ScannedProduct {
  if (products.length === 0) return FALLBACK_PRODUCTS[0]
  if (products.length === 1) return products[0]

  // Weight = inverse rank (first = main product, weighted heaviest)
  const weights = products.map((_, i) => Math.max(1, products.length - i) * 2)
  const total = weights.reduce((a, b) => a + b, 0)
  let r = Math.random() * total
  for (let i = 0; i < products.length; i++) {
    r -= weights[i]
    if (r <= 0) return products[i]
  }
  return products[0]
}
